/**
 * Returns the current timezone offset in hours (e.g., "-5.0").
 * @returns {string}
 */
export function getTimezone() {
    return (0 - new Date().getTimezoneOffset() / 60).toFixed(1);
}

/**
 * Deletes specified cache names from the Cache Storage API.
 * If no list is provided, defaults to deleting the 'page' cache.
 * @param {string[]} [flushList]
 */
export function updateContext(flushList) {
    if (!window.caches) return;
    const cacheList = flushList || ['page'];
    cacheList.forEach((cacheName) => {
        window.caches.delete(cacheName).then((removed) => removed);
    });
}

/**
 * Deletes a single entry (resourcePath) from a named cache.
 * @param {string} cacheType
 * @param {string} resourcePath
 */
export function deleteSpecificCacheEntry(cacheType, resourcePath) {
    if (!window.caches) return;
    window.caches
        .open(cacheType)
        .then((cache) => cache.delete(resourcePath))
        .then((removedStatus) => removedStatus);
}

/**
 * Parses the current window.location.search and returns an object of query parameters.
 * @returns {Record<string,string> | null}
 */
export function parseQueryString() {
    const search = window.location.search;
    if (!search || search.length < 2) return null;
    const query = search.substring(1);
    const retObj = {};
    query.split('&').forEach((pair) => {
        if (!pair) return;
        const [key, value] = pair.split('=');
        retObj[key] = decodeURIComponent(value || '');
    });
    return Object.keys(retObj).length ? retObj : null;
}

/**
 * Shuffles an array in place using Fisher-Yates and returns it.
 * @param {any[]} array
 * @returns {any[]}
 */
export function shuffle(array) {
    let counter = array.length;
    while (counter > 0) {
        const index = Math.floor(Math.random() * counter);
        counter--;
        [array[counter], array[index]] = [array[index], array[counter]];
    }
    return array;
}

/**
 * Returns true if the given element is fully within the viewport.
 * @param {Element} element
 * @returns {boolean}
 */
export function isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const vh = window.innerHeight || document.documentElement.clientHeight;
    return rect.top >= 0 && rect.left >= 0 && rect.bottom <= vh && rect.right <= vw;
}

/**
 * Returns the IANA time zone identifier of the user's environment, or an empty string on error.
 * @returns {string}
 */
export function getIANAtimeZone() {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return '';
    }
}

/**
 * Retrieves a value from window.hashTable by key, or undefined if not present.
 * @param {string} key
 * @returns {any}
 */
export function getHashValue(key) {
    const entry = window.hashTable && window.hashTable[key];
    return entry ? entry.value : undefined;
}

/**
 * Returns the index of val in arr, or -1 if not found.
 * Mirrors jQuery.inArray behavior.
 * @param {any} val
 * @param {any[]} arr
 * @returns {number}
 */
export function inArray(val, arr) {
    return Array.isArray(arr) ? arr.indexOf(val) : -1;
}

/**
 * Removes emoji characters from the input string.
 * @param {string} value
 * @returns {string}
 */
export function trimEmojis(value) {
    if (!value) return '';
    const emojiRegex =
        /(?:[\u2700-\u27bf]|(?:\ud83c[\udde6-\uddff]){2}|[\ud800-\udbff][\udc00-\udfff]|[\u0023-\u0039]\ufe0f?\u20e3|\u3299|\u3297|\u303d|\u3030|\u24c2|\ud83c[\udd70-\udd71]|\ud83c[\udd7e-\udd7f]|\ud83c\udd8e|\ud83c[\udd91-\udd9a]|\ud83c[\udde6-\uddff]|[\ud83c\ude01-\ude02]|\ud83c\ude1a|\ud83c\ude2f|[\ud83c\ude32-\ude3a]|[\ud83c\ude50-\ude51]|\u203c|\u2049|[\u25aa-\u25ab]|\u25b6|\u25c0|[\u25fb-\u25fe]|\u00a9|\u00ae|\u2122|\u2139|\ud83c\udc04|[\u2600-\u26FF]|\u2b05|\u2b06|\u2b07|\u2b1b|\u2b1c|\u2b50|\u2b55|\u231a|\u231b|\u2328|\u23cf|[\u23e9-\u23f3]|[\u23f8-\u23fa]|\ud83c\udccf|\u2934|\u2935|\u0000|[\u2190-\u21ff])/g;
    return value.replace(emojiRegex, '');
}
